import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Grid2,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { actions, addButton, mainCont } from "./sxprops";
import { SearchField } from "../../common/components/search-field";
import useMainStore from "../landing-page/api/store";
import { useGetPagedUserManagementUsers } from "./api/hooks";
import { Add } from "@mui/icons-material";
import { addProductButton } from "../products/sxprops";
import { DataTable } from "./components/data-table";
import { Handlers } from "./api/handlers";
import useUserManagementStore from "./api/store";
import { UserInvModal } from "../../common/components/user-inv-modal";
import {
  UserFormSchema,
  userInvitationSchema,
  UserSchema,
} from "../../common/components/user-inv-modal/api/schema";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { initialUserData } from "../../common/components/user-inv-modal/api/types";
import { ModalType } from "../../common/components/user-invitation-modal/api/types";
import { useGetSpecificRole } from "../../common/api/roles/hooks";
import { pascalToRegular, UserRoles } from "../../common/enums/user-roles";
import { SuccessModal } from "../../common/components/success-modal";
import { AddUserModal } from "./components/add-modal";
import { AddGroupsModal } from "./components/select-groups-modal";
import useSuccessModalStore from "../../common/components/success-modal/api/store";
import { useLocation, useParams } from "react-router-dom";
import { LockUserModal } from "./components/lock-user-modal";
import { getHighestRole } from "../../common/utils/role-identifier";
import { headerCont } from "../clients/sxprops";
import {
  getBizboxUserPrivileges,
  getGroupAdminPrivileges,
} from "../../common/auth/privileges";
import { useAuthStore } from "../../common/auth/store";
import useAccessibilityModalStore from "./manage-access/store";
import ManageAccessModal from "./manage-access";
import { groupAdminsSteps, bizboxUsersSteps } from "./components/walkthrough-types";
import { groupAdminsQuizQuestions, bizboxUsersQuizQuestions } from "./components/quiz";
import { useModuleWalkthrough } from "../../common/components/walkthroughV3/useModuleWalkthrough";
import WalkthroughTrigger from "../../common/components/walkthroughV3/walkthrough-trigger";
import useUserInvStore from "../../common/components/user-inv-modal/api/store";
import useLandingPageStore from "../landing-page/store";
import queryClient from "../../common/query-client";
import PasswordConfirmDialog from "./components/modals/confirm-passw-modal";
import CredentialsSentDialog from "./components/modals/credentials-sent-modal";
import { DeleteUserModal } from "./components/modals/delete-user-modal";
import { AssignInternalProductsModal } from "./components/assign-products-modal";
import { enqueueSnackbar } from "notistack";

type Modes = "client" | "bizbox";

const UserManagement: React.FC = () => {
  const { mode } = useParams<{ mode: Modes }>();

  // decide your boolean however you like:
  const isInternal = mode === "bizbox";

  const theme = useTheme();

  const authStore = useAuthStore();
  const groupAdminPrivileges = getGroupAdminPrivileges(authStore.privileges);
  const bizboxUserPrivileges = getBizboxUserPrivileges(authStore.privileges);
  const isTablet = useMediaQuery(theme.breakpoints.only("tablet"));
  const isMobile = useMediaQuery(theme.breakpoints.only("mobile"));

  const mainStore = useMainStore();
  const accessibilityModalState = useAccessibilityModalStore();

  const hasUMAccess =
    mainStore.currentUser?.roles?.includes("SuperAdmin") ||
    mainStore.currentUser?.roles?.includes("GroupAdmin") ||
    mainStore.currentUser?.roles?.includes("BizboxAdmin");

  // const hasBizboxAccess =
  //   mainStore.currentUser?.roles?.includes("SuperAdmin") ||
  //   mainStore.currentUser?.roles?.includes("BizboxAdmin");
  // const isGroupAdmin = mainStore.currentUser?.roles?.includes("GroupAdmin");

  const handlers = new Handlers();
  const userManagementstore = useUserManagementStore();

  const onRoleLookupError = () => {
    enqueueSnackbar(
      "Couldn't load the role details needed to invite this user — please try again.",
      { variant: "error" },
    );
  };

  const { control, resetField, handleSubmit } = useForm({
    defaultValues: {
      query: userManagementstore.initialUserManagementState.searchText,
    },
  });

  useEffect(() => {
    resetField("query", { defaultValue: "" });

    userManagementstore.setSearchText("");
    userManagementstore.setUserManagementPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const { isShowModal, setShowModal } = useSuccessModalStore();

  const userInvModalForm = useForm<UserSchema>({
    resolver: zodResolver(userInvitationSchema),
    defaultValues: initialUserData,
  });

  const onSubmit = (queryData: FieldValues) => {
    userManagementstore.setUserManagementPage(1);
    userManagementstore.setSearchText(queryData.query);
  };

  const location = useLocation();

  ///<-------WALKTHROUGH V3 ------->
  const cancelUserButtonRef = useRef<HTMLButtonElement | null>(null);
  const userInvStore = useUserInvStore();
  const { setOpenModal, setOpenUserManagement } = useLandingPageStore();
  const [showGroupAdminsActionsMenu, setShowGroupAdminsActionsMenu] = useState(false);
  const [showBizboxUsersActionsMenu, setShowBizboxUsersActionsMenu] = useState(false);
  const showUserActionsMenu = isInternal ? showBizboxUsersActionsMenu : showGroupAdminsActionsMenu;

  const isInternalUser =
    mainStore.currentUser?.roles?.includes("SuperAdmin") ||
    mainStore.currentUser?.roles?.includes("BizboxAdmin") ||
    mainStore.currentUser?.roles?.includes("BizboxUser");

  const isSuperAdmin = !!mainStore.currentUser?.roles?.includes("SuperAdmin");
  const isBizboxAdmin = !!mainStore.currentUser?.roles?.includes("BizboxAdmin");

  const { walkthroughUI: groupAdminsWalkthroughUI, handleLightbulbClick: handleGroupAdminsLightbulb, isWalkthroughActive: isGroupAdminsActive } = useModuleWalkthrough({
    steps: groupAdminsSteps({ isSuperAdmin, isBizboxAdmin }),
    questions: groupAdminsQuizQuestions,
    storageKey: "group-admins-walkthrough",
    trackKey: "GroupAdmins",
    isEnabled: (!isInternalUser || isSuperAdmin || isBizboxAdmin) && !isInternal,
    onStart: () => {
      setOpenModal(true);
      setOpenUserManagement(true);
    },
    onNext: (_step, index) => {
      switch (index) {
        case 4:
          userInvStore.setIsAddingGroupAdmin(true);
          userInvStore.setModalType(ModalType.QueryForm);
          handlers.handleOpenUserInvModal();
          break;
        case 5:
          userInvStore.setWalkthroughMode(false);
          userInvStore.setModal(false);
          userInvStore.setModalType(ModalType.QueryForm);
          userManagementstore.setAddModal(true);
          break;
        case 6:
          userManagementstore.setAddModal(false);
          setShowGroupAdminsActionsMenu(true);
          break;
        case 7:
          setShowGroupAdminsActionsMenu(false);
          break;
      }
    },
    onPrev: (_step, index) => {
      switch (index) {
        case 5:
          userInvStore.setWalkthroughMode(false);
          userInvStore.setModal(false);
          break;
        case 6:
          userManagementstore.setAddModal(false);
          userInvStore.setIsAddingGroupAdmin(true);
          userInvStore.setModalType(ModalType.QueryForm);
          handlers.handleOpenUserInvModal();
          break;
        case 7:
          setShowGroupAdminsActionsMenu(false);
          break;
      }
    },
    onCloseAll: () => {
      userInvStore.setWalkthroughMode(false);
      userInvStore.setModal(false);
      userManagementstore.setAddModal(false);
      setShowGroupAdminsActionsMenu(false);
    },
    quizTitle: "Group Admins Module Knowledge Check",
    moduleName: "Group Admins",
  });

  const { walkthroughUI: bizboxUsersWalkthroughUI, handleLightbulbClick: handleBizboxUsersLightbulb, isWalkthroughActive: isBizboxUsersActive } = useModuleWalkthrough({
    steps: bizboxUsersSteps({ isSuperAdmin, isBizboxAdmin }),
    questions: bizboxUsersQuizQuestions,
    storageKey: "bizbox-users-walkthrough",
    trackKey: "BizboxUsers",
    isEnabled: (isSuperAdmin || isBizboxAdmin) && isInternal,
    onStart: () => {
      setOpenModal(true);
      setOpenUserManagement(true);
    },
    onNext: (_step, index) => {
      switch (index) {
        case 3:
          userInvStore.setIsAddingBizBoxUser(true);
          userInvStore.setModalType(ModalType.QueryForm);
          handlers.handleOpenUserInvModal();
          break;
        case 4:
          userInvStore.setWalkthroughMode(false);
          userInvStore.setModal(false);
          userInvStore.setModalType(ModalType.QueryForm);
          userManagementstore.setAddModal(true);
          break;
        case 5:
          userManagementstore.setAddModal(false);
          setShowBizboxUsersActionsMenu(true);
          break;
        case 6:
          setShowBizboxUsersActionsMenu(false);
          break;
      }
    },
    onPrev: (_step, index) => {
      switch (index) {
        case 4:
          userInvStore.setWalkthroughMode(false);
          userInvStore.setModal(false);
          break;
        case 5:
          userManagementstore.setAddModal(false);
          userInvStore.setIsAddingBizBoxUser(true);
          userInvStore.setModalType(ModalType.QueryForm);
          handlers.handleOpenUserInvModal();
          break;
        case 6:
          setShowBizboxUsersActionsMenu(false);
          break;
      }
    },
    onCloseAll: () => {
      userInvStore.setWalkthroughMode(false);
      userInvStore.setModal(false);
      userManagementstore.setAddModal(false);
      setShowBizboxUsersActionsMenu(false);
    },
    quizTitle: "BizBox Users Module Knowledge Check",
    moduleName: "BizBox Users",
  });

  const isWalkthroughActive = isGroupAdminsActive || isBizboxUsersActive;

  ///<-------END ------->

  const BizboxAdminRole = useGetSpecificRole(
    UserRoles.BizboxAdmin,
    isWalkthroughActive,
  );
  const coGroupAdminRole = useGetSpecificRole(
    UserRoles.CoGroupAdmin,
    isWalkthroughActive,
  );
  const bizboxUser = useGetSpecificRole(
    UserRoles.BizboxUser,
    isWalkthroughActive,
  );

  const getPagedBizboxUsers = useGetPagedUserManagementUsers(
    isInternal,
    isWalkthroughActive,
  );

  const CSAccess =
    "Bizbox Admin" ===
    (mainStore.currentUser?.roles &&
      pascalToRegular(getHighestRole(mainStore.currentUser?.roles)));

  const groupAdminAccess =
    groupAdminPrivileges.invite &&
    location.pathname === "/user-management/client";
  const bizboxUserAccess =
    (bizboxUserPrivileges.create || bizboxUserPrivileges.createNewCoAdmin) &&
    location.pathname === "/user-management/bizbox";

  const hasInviteAccess = groupAdminAccess || bizboxUserAccess;

  return (
    <Box sx={mainCont}>
      <Box sx={headerCont}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4" color="primary.main">
            User Managements - {isInternal ? "BizBox Users" : "Group Admins"}
          </Typography>

          {!isInternal && (!isInternalUser || isSuperAdmin || isBizboxAdmin) && (
            <WalkthroughTrigger id="group-admin-walkthrough-start-button" onClick={handleGroupAdminsLightbulb} />
          )}
          {isInternal && (isSuperAdmin || isBizboxAdmin) && (
            <WalkthroughTrigger id="bizbox-users-walkthrough-start-button" onClick={handleBizboxUsersLightbulb} />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" textAlign="start">
          Invite co-admins to assist in managing clients and their subscribed
          products.
        </Typography>
      </Box>
      <Grid2 container sx={headerCont} rowGap={1}>
        <Grid2
          id="group-admin-search-bar"
          data-testid="group-admin-search-bar"
          size={{ mobile: 8.5, laptop: 5 }}
          mr={2}
        >
          <SearchField
            testIdPrefix="user-mgmt"
            control={control}
            isFetching={getPagedBizboxUsers.isFetching}
            resetField={resetField}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
          />
        </Grid2>

        {(isWalkthroughActive || (hasUMAccess && hasInviteAccess)) && (
          <Grid2
            id="group-admin-invite-button"
            size={{ mobile: 1, tablet: 2, laptop: 1.5 }}
          >
            <Tooltip title={isTablet ? "Add User" : ""}>
              <>
                {isMobile ? (
                  <IconButton
                    id="invite-user-button"
                  data-testid="invite-user-button"
                    onClick={() => {
                      if (isInternal) {
                        userInvStore.setIsAddingBizBoxUser(true);
                      } else {
                        userInvStore.setIsAddingGroupAdmin(true);
                      }
                      handlers.handleOpenUserInvModal();
                    }}
                    sx={{ ...addButton(isMobile), minWidth: "100px" }}
                  >
                    <Add sx={{ color: "white" }} />
                  </IconButton>
                ) : (
                  <Button
                    id="invite-user-button"
                  data-testid="invite-user-button"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => {
                      if (isInternal) {
                        userInvStore.setIsAddingBizBoxUser(true);
                      } else {
                        userInvStore.setIsAddingGroupAdmin(true);
                      }
                      handlers.handleOpenUserInvModal();
                    }}
                    sx={addButton(isMobile)}
                  >
                    Invite User
                  </Button>
                )}
              </>
            </Tooltip>
          </Grid2>
        )}
      </Grid2>
      <Box
        id="group-admin-data-table"
        data-testid="group-admin-data-table"
        sx={{
          width: "100%",
          height: `calc(100% - 280px)`,
          mt: "10px",
        }}
      >
        <DataTable
          getPagedBizboxUsers={getPagedBizboxUsers}
          isInternal={isInternal}
          walkthroughMenuOpen={showUserActionsMenu}
        />
      </Box>
      {!userManagementstore.isEditModalOpen && (
        <UserInvModal
          walkthroughMode={isWalkthroughActive}
          isInternal={isInternal}
          formSchema={userInvModalForm}
          onInviteNew={(_val) => {
            const openCreateWithRoleOnly = (data?: {
              id?: string;
              name?: string;
              displayName?: string;
            }) => {
              if (!data?.id || !data?.name || !data?.displayName) {
                enqueueSnackbar(
                  "Couldn't load the role details needed to invite this user — please try again.",
                  { variant: "error" },
                );
                return;
              }

              handlers.handleCloseUserInvModal(userInvModalForm.reset);

              handlers.handleOpenModal({
                role: {
                  id: data.id,
                  name: data.name,
                  displayName: data.displayName,
                  role: data.name,
                },
              });
            };

            if (!isInternal) {
              coGroupAdminRole.mutate("CoGroupAdmin", {
                onSuccess: (data) => {
                  openCreateWithRoleOnly(data);
                },
                onError: onRoleLookupError,
              });
            } else {
              if (CSAccess) {
                bizboxUser.mutate("BizboxUser", {
                  onSuccess: (data) => {
                    openCreateWithRoleOnly(data);
                  },
                  onError: onRoleLookupError,
                });
              } else {
                BizboxAdminRole.mutate("BizboxAdmin", {
                  onSuccess: (data) => {
                    openCreateWithRoleOnly(data);
                  },
                  onError: onRoleLookupError,
                });
              }
            }
          }}
          onSelectActiveRecord={(val) => {
            const applyRoleAndOpen = (data?: {
              id?: string;
              name?: string;
              displayName?: string;
            }) => {
              if (!data?.id || !data?.name || !data?.displayName) {
                onRoleLookupError();
                return;
              }
              val.role = {
                id: data.id,
                name: data.name,
                displayName: data.displayName,
                role: data.name,
              };
              userManagementstore.setSelectedUser(val);
              handlers.handleCloseUserInvModal(userInvModalForm.reset);
              handlers.handleOpenModal(val);
            };

            if (!isInternal) {
              coGroupAdminRole.mutate("CoGroupAdmin", {
                onSuccess: applyRoleAndOpen,
                onError: onRoleLookupError,
              });
            } else {
              if (CSAccess) {
                bizboxUser.mutate("BizboxUser", {
                  onSuccess: applyRoleAndOpen,
                  onError: onRoleLookupError,
                });
              } else {
                BizboxAdminRole.mutate("BizboxAdmin", {
                  onSuccess: applyRoleAndOpen,
                  onError: onRoleLookupError,
                });
              }
            }
          }}
          onNoResultFound={(val) => {
            const openCreateWithRole = (roleData?: {
              id?: string;
              name?: string;
              displayName?: string;
            }) => {
              if (!roleData?.id || !roleData?.name || !roleData?.displayName) {
                onRoleLookupError();
                return;
              }

              const prefillUser: Partial<UserFormSchema> = {
                ...val,
                role: {
                  id: roleData.id,
                  name: roleData.name,
                  displayName: roleData.displayName,
                  role: roleData.name,
                },
              };

              handlers.handleCloseUserInvModal(userInvModalForm.reset);
              handlers.handleOpenModal(prefillUser);
            };

            if (!isInternal) {
              coGroupAdminRole.mutate("CoGroupAdmin", {
                onSuccess: openCreateWithRole,
                onError: onRoleLookupError,
              });
            } else {
              if (CSAccess) {
                bizboxUser.mutate("BizboxUser", {
                  onSuccess: openCreateWithRole,
                  onError: onRoleLookupError,
                });
              } else {
                BizboxAdminRole.mutate("BizboxAdmin", {
                  onSuccess: openCreateWithRole,
                  onError: onRoleLookupError,
                });
              }
            }
          }}
        />
      )}
      {<PasswordConfirmDialog />}
      {<CredentialsSentDialog />}
      <DeleteUserModal />
      <LockUserModal isInternal={isInternal} />
      <AddGroupsModal isInternal={isInternal} />
      <AddUserModal
        isInternal={isInternal}
        isWalkthroughMode={isWalkthroughActive}
        cancelUserButtonRef={cancelUserButtonRef}
      />
      {accessibilityModalState.isManageAccessModalOpen && <ManageAccessModal />}
      {isInternal && <AssignInternalProductsModal />}
      {isShowModal && (
        <SuccessModal
          message="Login Information Successfully Sent"
          walktroughMode={isWalkthroughActive}
        />
      )}
      {groupAdminsWalkthroughUI}
      {bizboxUsersWalkthroughUI}
    </Box>
  );
};
export default UserManagement;
